"use client";

import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { useVehicles } from "@/context/VehicleContext";
import { Plus } from "lucide-react";
import { Vehicle, VehicleType } from "@/types";
import { toast } from "sonner";
import CommonHeader from "./common/CommonHeader";
import { VehicleSearchBar } from "./vehicle/VehicleSearchBar";
import { VehiclesTable } from "./vehicle/VehiclesTable";
import {
  VehicleFormDialog,
  VehicleFormData,
} from "./vehicle/VehicleFormDialog";
import { VehicleDetailDialog } from "./vehicle/VehicleDetailDialog";
import { VehicleDeleteDialog } from "./vehicle/VehicleDeleteDialog";
import {
  vehicleService,
  VehicleQueryParams,
} from "@/services/vehicle/vehicleService";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";
import {
  VehicleMakeOutput,
  VehicleModelOutput,
} from "@/types";
import { PaginationMeta } from "@/types/appointment.api.types";
import { useDebounce } from "@/hooks/useDebounce";

export function VehicleManagement() {
  const {
    customers,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    appointments,
  } = useApp();
  const { refreshVehicles } = useVehicles();

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Vehicle;
    direction: "asc" | "desc";
  }>({
    key: "plateNumber",
    direction: "asc",
  });
  const [queryParams, setQueryParams] = useState<VehicleQueryParams>({
    page: 1,
    limit: 10,
    sortBy: "plateNumber",
    sortOrder: "asc",
  });
  const [paginatedVehicles, setPaginatedVehicles] = useState<Vehicle[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [makes, setMakes] = useState<VehicleMakeOutput[]>([]);
  const [models, setModels] = useState<VehicleModelOutput[]>([]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const [isDeleteBlocked, setIsDeleteBlocked] = useState(false);
  const [deleteBlockDetails, setDeleteBlockDetails] = useState<any>(null);

  const [formData, setFormData] = useState<VehicleFormData>({
    customerId: "",
    plateNumber: "",
    makeId: "",
    modelId: "",
    year: new Date().getFullYear(),
    color: "",
    type: "sedan" as VehicleType,
    notes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch makes and models from API on component mount
  // Customers are loaded from CustomerContext (all pages)
  // Vehicles are fetched by VehicleContext
  useEffect(() => {
    fetchMakes();
    fetchModels();
  }, []);

  const fetchMakes = async () => {
    try {
      const data = await makeService.getAllMakes();
      // console.log(data);
      setMakes(data);
    } catch (err) {
      console.error("Error fetching makes:", err);
      toast.error("Failed to load vehicle makes. Please try again.");
    }
  };

  const fetchModels = async () => {
    try {
      const data = await modelService.getAllModels();
      setModels(data);
    } catch (err) {
      console.error("Error fetching models:", err);
      toast.error("Failed to load vehicle models. Please try again.");
    }
  };

  const handleMakeCreated = async (makeId: string) => {
    await fetchMakes();
    // The newly created make will be in the updated makes list
    // The VehicleFormDialog will handle auto-selection
  };

  const handleModelCreated = async (modelId: string) => {
    await fetchModels();
    // The newly created model will be in the updated models list
    // The VehicleFormDialog will handle auto-selection
  };

  const mapApiVehicleToViewModel = (v: any): Vehicle => ({
    id: v.id,
    customerId: v.userId || v.user_id || v.customerId || v.customer_id || "",
    plateNumber: v.plateNumber || v.plate_number || "",
    make:
      (typeof v.Make === "object" && v.Make?.name) ||
      (typeof v.make === "object" && v.make?.name) ||
      v.make ||
      v.makeName ||
      "",
    model:
      (typeof v.Model === "object" && v.Model?.name) ||
      (typeof v.model === "object" && v.model?.name) ||
      v.model ||
      v.modelName ||
      "",
    color: v.color || "",
    type: ((v.type || "sedan").toLowerCase() as VehicleType) || "sedan",
    totalServices: v.totalServices || v.total_services || 0,
    lastService: v.lastService ? new Date(v.lastService) : undefined,
    year: v.year || null,
    notes: v.notes || null,
  });

  const fetchVehiclesPaginated = useCallback(async () => {
    try {
      const response = await vehicleService.getVehiclesPaginated(queryParams);
      setPaginatedVehicles(response.data.map(mapApiVehicleToViewModel));
      setPagination(response.pagination);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch vehicles";
      console.error("Error fetching vehicles:", error);
      toast.error(errorMessage);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchVehiclesPaginated();
  }, [fetchVehiclesPaginated]);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      page: 1,
      search: debouncedSearch.trim() || undefined,
    }));
  }, [debouncedSearch]);

  const handleSort = (key: keyof Vehicle) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      setQueryParams((current) => ({
        ...current,
        page: 1,
        sortBy: key as VehicleQueryParams["sortBy"],
        sortOrder: direction,
      }));
      return {
        key,
        direction,
      };
    });
  };

  const resetForm = () => {
    setFormData({
      customerId: "",
      plateNumber: "",
      makeId: "",
      modelId: "",
      year: new Date().getFullYear(),
      color: "",
      type: "sedan",
      notes: "",
    });
  };

  // Map VehicleType to API format
  const mapVehicleTypeToAPI = (type: VehicleType): string => {
    const typeMap: Record<VehicleType, string> = {
      sedan: "Sedan",
      suv: "SUV",
      truck: "Truck",
      van: "Van",
    };
    return typeMap[type];
  };

  const handleAdd = async () => {
    try {
      if (!formData.customerId || !formData.plateNumber) {
        toast.error("Please fill in all required fields");
        return;
      }

      setIsSubmitting(true);

      // Use context's addVehicle method which handles API call and state update
      await addVehicle({
        customerId: formData.customerId,
        plateNumber: formData.plateNumber,
        color: formData.color,
        type: formData.type,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
        notes: formData.notes || undefined,
      });

      // Refresh vehicles explicitly to ensure table updates
      await refreshVehicles();
      await fetchVehiclesPaginated();

      // Close dialog and reset form after successful creation
      setIsAddDialogOpen(false);
      resetForm();
    } catch (err: any) {
      console.error("Error adding vehicle:", err);
      // Error toast is already handled in the context's addVehicle method
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedVehicle || !formData.plateNumber) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsSubmitting(true);

      // Transform form data to API payload format
      const payload = {
        type: mapVehicleTypeToAPI(formData.type || "sedan"),
        plateNumber: formData.plateNumber,
        color: formData.color,
        notes: formData.notes || null,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
      };

      await vehicleService.updateVehicle(selectedVehicle.id, payload);

      // Update context state with the changes
      updateVehicle(selectedVehicle.id, {
        plateNumber: formData.plateNumber,
        color: formData.color,
        type: formData.type,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
        notes: formData.notes || undefined,
      });

      // Refresh vehicles to get latest data from server
      await refreshVehicles();
      await fetchVehiclesPaginated();

      toast.success("Vehicle updated successfully");

      setIsEditDialogOpen(false);
      setSelectedVehicle(null);
      resetForm();
    } catch (err: any) {
      console.error("Error updating vehicle:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to update vehicle. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;

    try {
      await vehicleService.deleteVehicle(selectedVehicle.id);

      // Update context state
      deleteVehicle(selectedVehicle.id);

      // Refresh vehicles to ensure state is in sync
      await refreshVehicles();
      await fetchVehiclesPaginated();

      toast.success("Vehicle deleted successfully");

      setIsDeleteDialogOpen(false);
      setSelectedVehicle(null);
    } catch (err: any) {
      console.error("Error deleting vehicle:", err);

      // Handle deletion blocked error
      if (err?.response?.data?.code === "DELETION_BLOCKED") {
        setDeleteBlockDetails(err.response.data.details);
        setIsDeleteBlocked(true);
        return; // Don't close dialog
      }

      toast.error(
        err?.response?.data?.message ||
          "Failed to delete vehicle. Please try again.",
      );
    }
  };

  const normalizeName = (value?: string) => (value || "").trim().toLowerCase();

  const findMakeIdByName = (makeName?: string) => {
    const normalized = normalizeName(makeName);
    if (!normalized) return "";
    const match = makes.find(
      (make) => normalizeName(make.name) === normalized,
    );
    return match?.id || "";
  };

  const findModelIdByName = (modelName?: string, makeId?: string) => {
    const normalized = normalizeName(modelName);
    if (!normalized || !makeId) return "";
    const match = models.find(
      (model) =>
        model.makeId === makeId &&
        normalizeName(model.name) === normalized,
    );
    return match?.id || "";
  };

  const openEditDialog = (vehicle: Vehicle) => {
    const resolvedMakeId = vehicle.makeId || findMakeIdByName(vehicle.make);
    const resolvedModelId =
      vehicle.modelId || findModelIdByName(vehicle.model, resolvedMakeId);

    setSelectedVehicle(vehicle);
    setFormData({
      customerId: vehicle.customerId,
      plateNumber: vehicle.plateNumber,
      makeId: resolvedMakeId,
      modelId: resolvedModelId,
      year: vehicle.year || new Date().getFullYear(), // Use year from vehicle or default
      color: vehicle.color,
      type: vehicle.type,
      notes: vehicle.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const openDetailDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailDialogOpen(true);
  };

  const openDeleteDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDeleteBlocked(false);
    setDeleteBlockDetails(null);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonHeader
        title="Vehicle Management"
        description="Manage customer vehicles"
        onClick={() => setIsAddDialogOpen(true)}
        buttonText="Add Vehicle"
        buttonIcon={Plus}
      />

      {/* Search */}
      <VehicleSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* Vehicles Table */}
      <VehiclesTable
        vehicles={paginatedVehicles}
        customers={customers}
        sortConfig={sortConfig}
        onSort={handleSort}
        onView={openDetailDialog}
        onEdit={openEditDialog}
        onDelete={openDeleteDialog}
        query={queryParams}
        pagination={pagination}
        onQueryChange={setQueryParams}
      />

      {/* Add Vehicle Dialog */}
      <VehicleFormDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleAdd}
        onCancel={() => {
          setIsAddDialogOpen(false);
          resetForm();
        }}
        mode="add"
        customers={customers}
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
      />

      {/* Edit Vehicle Dialog */}
      <VehicleFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleEdit}
        onCancel={() => {
          setIsEditDialogOpen(false);
          resetForm();
        }}
        mode="edit"
        customers={customers}
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
      />

      {/* Vehicle Detail Dialog */}
      <VehicleDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        vehicle={selectedVehicle}
        customers={customers}
        appointments={appointments}
      />

      {/* Delete Confirmation Dialog */}
      <VehicleDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        vehicle={selectedVehicle}
        onConfirm={handleDelete}
        isBlocked={isDeleteBlocked}
        blockDetails={deleteBlockDetails}
      />
    </div>
  );
}
